import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';
import { STAGES, INSURERS } from '../lib/constants';
import { decodeVin } from '../lib/vin';
import VinScanner from './VinScanner';

const IS_DESKTOP = () => window.innerWidth >= 768;

export default function NewClaimModal({ session, room, onClose, onCreated }) {
  const desktop = IS_DESKTOP();
  const [form, setForm] = useState({
    car: '', plate: '', color: '', vin: '',
    client_name: '', client_phone: '',
    insurer: '', claim_num: '', adjuster: '', adjuster_phone: '', adjuster_email: '',
    car_in: new Date().toISOString().split('T')[0],
    car_out: '',
    status: 'Car In',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [vinLookup, setVinLookup] = useState(''); // '' | 'loading' | error/info text
  const [photos, setPhotos] = useState([]); // intake photos, uploaded after the claim is created

  function set(field, val) {
    setForm(f => ({ ...f, [field]: val }));
  }

  // Warn before closing if the user has typed anything (car_in + status are prefilled)
  function tryClose() {
    const dirty = photos.length > 0 || Object.entries(form).some(([k, v]) => k !== 'car_in' && k !== 'status' && String(v).trim() !== '');
    if (dirty && !window.confirm('Discard this new claim?')) return;
    onClose();
  }

  // Scanned a VIN → fill it, then auto-decode Year/Make/Model via NHTSA.
  async function handleScan(vin) {
    setScanning(false);
    set('vin', vin);
    setVinLookup('loading');
    try {
      const info = await decodeVin(vin);
      if (info.car) {
        set('car', info.car);
        setVinLookup('');
      } else {
        setVinLookup('VIN saved — couldn’t auto-detect vehicle, enter make/model manually');
      }
    } catch {
      setVinLookup('VIN saved — lookup failed, enter make/model manually');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Duplicate guard: warn if this plate or VIN already has an open claim
    const plate = form.plate.trim().replace(/,/g, '');
    const vin = form.vin.trim().replace(/,/g, '');
    if (plate || vin) {
      const ors = [];
      if (plate) ors.push(`plate.ilike.${plate}`);
      if (vin) ors.push(`vin.ilike.${vin}`);
      const { data: dupes } = await supabase.from('claims')
        .select('id, car, plate, status')
        .eq('room_id', room.id)
        .neq('status', 'Closed')
        .or(ors.join(','));
      if (dupes && dupes.length > 0) {
        const d = dupes[0];
        if (!window.confirm(`${d.car || 'A car'} (${d.plate || 'no plate'}) already has an open claim — currently "${d.status}". Create another claim anyway?`)) {
          setLoading(false);
          return;
        }
      }
    }

    const { data, error: err } = await supabase
      .from('claims')
      .insert({ ...form, room_id: room.id, created_by: session.user.id })
      .select()
      .single();

    if (err) { setError(err.message); setLoading(false); return; }

    // Upload intake photos into the claim's file folder
    if (photos.length > 0) {
      let failed = 0;
      for (const file of photos) {
        const path = `${room.id}/${data.id}/${Date.now()}-${file.name}`;
        const { error: upErr } = await supabase.storage.from('claim-files').upload(path, file);
        if (upErr) failed++;
      }
      if (failed > 0) toast(`${failed} photo${failed > 1 ? 's' : ''} failed to upload — add them from the Files tab.`, 'error');
      else toast(`${photos.length} intake photo${photos.length > 1 ? 's' : ''} saved to the claim.`, 'success');
    }

    supabase.from('activity_log').insert({
      room_id: room.id,
      claim_id: data.id,
      user_id: session.user.id,
      user_name: session.user.user_metadata?.full_name || session.user.email,
      action: 'claim_created',
      detail: `created claim for ${form.car || form.client_name || 'vehicle'}`,
    }).then(() => {});

    onCreated(data);
  }

  const pairGrid = { display: 'grid', gridTemplateColumns: desktop ? '1fr 1fr' : '1fr', gap: 10, marginTop: 10 };

  return (
    <div className="modal-overlay" style={{
      zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: desktop ? 32 : 0, background: 'rgba(5,5,6,0.85)',
    }} onClick={e => e.target === e.currentTarget && tryClose()}>
      <div className="modal-panel" style={{
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        width: '100%',
        maxWidth: 900,
        maxHeight: desktop ? '92vh' : undefined,
      }}>
        {/* Header */}
        <div style={{
          height: 72, padding: '0 24px',
          boxShadow: 'inset 0 -9px 9px -9px rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'center', position: 'relative', flexShrink: 0,
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: desktop ? 26 : 19, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', position: 'absolute', left: '50%', transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>New Claim</div>
          <button type="button" onClick={tryClose} className="raise-3d" style={{ background: 'var(--bg3)', border: 'none', color: 'var(--text2)', borderRadius: '50%', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', marginLeft: 'auto' }}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Body — two columns on desktop, single column on mobile */}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="modal-scroll" style={{ flex: 1, padding: desktop ? 24 : 16, display: 'flex', flexDirection: 'column', gap: desktop ? 24 : 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: desktop ? '1fr 1fr' : '1fr', gap: desktop ? 24 : 18 }}>

              {/* Left column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <Section title="Vehicle">
                  <div className="field">
                    <label className="label">Year / Make / Model</label>
                    <input value={form.car} onChange={e => set('car', e.target.value)} placeholder="e.g. 2021 Toyota Camry" />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
                    <div className="field">
                      <label className="label">License Plate</label>
                      <input value={form.plate} onChange={e => set('plate', e.target.value.toUpperCase())} placeholder="ABC1234" style={{ fontFamily: 'var(--font-mono)' }} />
                    </div>
                    <div className="field">
                      <label className="label">Color</label>
                      <input value={form.color} onChange={e => set('color', e.target.value)} placeholder="Silver" />
                    </div>
                  </div>
                  <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">VIN</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={form.vin} onChange={e => set('vin', e.target.value.toUpperCase())} placeholder="17-digit VIN" style={{ fontFamily: 'var(--font-mono)', fontSize: 13, flex: 1, minWidth: 0 }} />
                      <button type="button" onClick={() => { setVinLookup(''); setScanning(true); }} title="Scan VIN barcode" className="raise-3d-orange" style={{ flexShrink: 0, background: 'var(--orange)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#16100b', padding: '0 14px', display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2"/><line x1="6" y1="12" x2="18" y2="12"/></svg>
                        Scan
                      </button>
                    </div>
                    {vinLookup === 'loading' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="spinner" style={{ width: 12, height: 12 }} /> Looking up VIN…
                      </div>
                    )}
                    {vinLookup && vinLookup !== 'loading' && (
                      <div style={{ marginTop: 6, fontSize: 12, color: 'var(--yellow)' }}>{vinLookup}</div>
                    )}
                  </div>
                </Section>

                <Section title="Client">
                  <div className="field">
                    <label className="label">Name</label>
                    <input value={form.client_name} onChange={e => set('client_name', e.target.value)} placeholder="Full name" />
                  </div>
                  <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">Phone</label>
                    <input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} placeholder="(555) 555-5555" type="tel" />
                  </div>
                </Section>

                <Section title="Intake Photos">
                  <label style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '13px 14px', background: 'var(--bg3)', border: '1px dashed var(--border)',
                    borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text2)',
                  }}>
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    {photos.length > 0 ? `${photos.length} photo${photos.length > 1 ? 's' : ''} — add more` : 'Add damage photos'}
                    <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                      onChange={e => { setPhotos(prev => [...prev, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
                  </label>
                  {photos.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                      {photos.map((f, i) => (
                        <div key={i} style={{ position: 'relative', width: 64, height: 64, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border)' }}>
                          <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          <button type="button" onClick={() => setPhotos(prev => prev.filter((_, j) => j !== i))}
                            style={{ position: 'absolute', top: 2, right: 2, width: 20, height: 20, borderRadius: '50%', background: 'rgba(0,0,0,0.7)', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                </Section>
              </div>

              {/* Right column */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <Section title="Insurance">
                  <div className="field">
                    <label className="label">Company</label>
                    <input value={form.insurer} onChange={e => set('insurer', e.target.value)} placeholder="State Farm" list="insurer-suggestions" />
                    <datalist id="insurer-suggestions">
                      {INSURERS.map(ins => <option key={ins.name} value={ins.name} />)}
                    </datalist>
                  </div>
                  <div style={pairGrid}>
                    <div className="field">
                      <label className="label">Claim #</label>
                      <input value={form.claim_num} onChange={e => set('claim_num', e.target.value)} placeholder="Claim number" style={{ fontFamily: 'var(--font-mono)' }} />
                    </div>
                    <div className="field">
                      <label className="label">Adjuster</label>
                      <input value={form.adjuster} onChange={e => set('adjuster', e.target.value)} placeholder="Adjuster's name" />
                    </div>
                  </div>
                  <div style={pairGrid}>
                    <div className="field">
                      <label className="label">Adjuster Phone</label>
                      <input value={form.adjuster_phone} onChange={e => set('adjuster_phone', e.target.value)} placeholder="(555) 555-5555" type="tel" />
                    </div>
                    <div className="field">
                      <label className="label">Adjuster Email</label>
                      <input value={form.adjuster_email} onChange={e => set('adjuster_email', e.target.value)} placeholder="adjuster@insurer.com" type="email" />
                    </div>
                  </div>
                </Section>

                <Section title="Dates & Status">
                  <div style={{ display: 'grid', gridTemplateColumns: desktop ? '1fr 1fr' : '1fr', gap: 10 }}>
                    <div className="field">
                      <label className="label">Car In</label>
                      <input type="date" value={form.car_in} onChange={e => set('car_in', e.target.value)} />
                    </div>
                    <div className="field">
                      <label className="label">Est. Car Out</label>
                      <input type="date" value={form.car_out} onChange={e => set('car_out', e.target.value)} />
                    </div>
                  </div>
                  <div className="field" style={{ marginTop: 10 }}>
                    <label className="label">Initial Status</label>
                    <select value={form.status} onChange={e => set('status', e.target.value)}>
                      {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </Section>

                <Section title="Notes">
                  <div className="field">
                    <textarea
                      value={form.notes}
                      onChange={e => set('notes', e.target.value)}
                      placeholder="Damage description, special instructions..."
                      rows={4}
                      style={{ resize: 'none' }}
                    />
                  </div>
                </Section>
              </div>
            </div>

            {error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{error}</div>}
          </div>

          {/* Footer — pinned */}
          <div style={{ display: 'flex', gap: 10, borderTop: '1px solid var(--border)', padding: desktop ? '16px 24px' : '14px 16px calc(14px + env(safe-area-inset-bottom))', flexShrink: 0 }}>
            <button type="button" className="btn btn-secondary" onClick={tryClose} style={{ flex: 1, padding: 14 }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading} style={{ flex: 2, padding: 14, fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 800, textTransform: 'uppercase' }}>
              {loading ? <div className="spinner" /> : 'Create Claim'}
            </button>
          </div>
        </form>
      </div>

      {scanning && <VinScanner onResult={handleScan} onClose={() => setScanning(false)} />}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-display)', fontSize: 12, fontWeight: 700,
        letterSpacing: '2.5px', textTransform: 'uppercase',
        color: 'var(--orange)', marginBottom: 10,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}
